import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Table, Button, Upload, Card, message, Popconfirm, Tag, Space, Typography,
  Row, Col, Alert, Empty, Modal, Select, Tooltip, Badge
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileMarkdownOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FolderOutlined,
  SwapOutlined,
  InboxOutlined,
  HistoryOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { fetchDocuments, uploadDocument, deleteDocument } from '../store/documentsSlice';
import { fetchFolders, createFolder, deleteFolder, moveDocument, setSelectedFolder } from '../store/foldersSlice';
import { addToQueue } from '../store/uploadQueueSlice';
import FolderTree from '../components/FolderTree';
import BulkUploadManager from '../components/BulkUploadManager';
import VersionHistoryDrawer from '../components/VersionHistoryDrawer';
import VersionCompareModal from '../components/VersionCompareModal';
import DocumentPreviewModal from '../components/DocumentPreviewModal';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

function Documents() {
  const dispatch = useDispatch();
  const { items: documents, loading } = useSelector((state) => state.documents);
  const { items: folders, selectedFolderId } = useSelector((state) => state.folders);
  const [uploading, setUploading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [processingDocId, setProcessingDocId] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [selectedDocForMove, setSelectedDocForMove] = useState(null);
  const [targetFolderId, setTargetFolderId] = useState(null);
  const [versionDrawerVisible, setVersionDrawerVisible] = useState(false);
  const [selectedDocForVersions, setSelectedDocForVersions] = useState(null);
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ docId: null, v1: null, v2: null });
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedDocForPreview, setSelectedDocForPreview] = useState(null);

  useEffect(() => {
    console.log('📁 Documents useEffect - fetching documents and folders');
    dispatch(fetchDocuments({}));
    console.log('📁 Dispatched fetchDocuments');
    dispatch(fetchFolders());
    console.log('📁 Dispatched fetchFolders');
  }, [dispatch]);

  useEffect(() => {
    if (!processingDocId) return;

    const intervalId = setInterval(async () => {
      try {
        await dispatch(fetchDocuments({})).unwrap();
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 1500);

    return () => clearInterval(intervalId);
  }, [processingDocId, dispatch]);

  useEffect(() => {
    if (!processingDocId) return;
    
    const processedDoc = documents.find(d => d.id === processingDocId);
    if (processedDoc && processedDoc.is_processed) {
      setProcessingDocId(null);
    }
  }, [documents, processingDocId]);

  const handleBeforeUpload = (file, fileList) => {
    if (fileList && fileList.length > 1) {
      const isFirst = fileList[0] === file;
      if (isFirst) {
        const folderId = viewMode === 'folder' ? selectedFolderId : null;
        dispatch(addToQueue({ files: fileList, folderId }));
        message.info(`${fileList.length} files added to upload queue`);
      }
    } else {
      const folderId = viewMode === 'folder' ? selectedFolderId : null;
      dispatch(addToQueue({ files: [file], folderId }));
      message.info(`${file.name} added to upload queue`);
    }
    return false;
  };

  const handleDelete = async (docId, filename) => {
    try {
      await dispatch(deleteDocument(docId)).unwrap();
      message.success(`${filename} deleted successfully`);
      await dispatch(fetchDocuments({}));
      setSelectedRowKeys(selectedRowKeys.filter(key => key !== docId));
    } catch (error) {
      message.error(`Delete failed: ${error}`);
    }
  };

  const handleBatchDelete = async () => {
    try {
      await Promise.all(
        selectedRowKeys.map(id => dispatch(deleteDocument(id)).unwrap())
      );
      message.success(`${selectedRowKeys.length} document(s) deleted successfully`);
      dispatch(fetchDocuments({}));
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(`Batch delete failed: ${error}`);
    }
  };

  const handleSelectFolder = (folderId, mode) => {
    dispatch(setSelectedFolder(folderId));
    setViewMode(mode);
  };

  const handleCreateFolder = async (name, parentFolderId) => {
    try {
      await dispatch(createFolder({ name, parentFolderId })).unwrap();
      message.success(`Folder "${name}" created successfully`);
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : error?.message || 'Failed to create folder';
      message.error(`${errorMsg}`);
      throw error; // Re-throw so FolderTree knows creation failed
    } finally {
      // Always refresh folders to show latest state
      try {
        await dispatch(fetchFolders());
      } catch (err) {
        console.error('Failed to refresh folders:', err);
      }
    }
  };

  const handleDeleteFolder = async (folderId) => {
    console.log('🗑️ handleDeleteFolder called with folderId:', folderId);
    try {
      console.log('🗑️ Dispatching deleteFolder action...');
      const result = await dispatch(deleteFolder(folderId)).unwrap();
      console.log('🗑️ deleteFolder result:', result);
      message.success('Folder deleted');
      console.log('🗑️ Refreshing folders...');
      await dispatch(fetchFolders());
      console.log('🗑️ Refreshing documents...');
      await dispatch(fetchDocuments({}));
      if (selectedFolderId === folderId) {
        console.log('🗑️ Clearing selected folder...');
        dispatch(setSelectedFolder(null));
        setViewMode('all');
      }
      console.log('🗑️ Delete complete!');
    } catch (error) {
      console.error('🗑️ Delete folder error:', error);
      console.error('🗑️ Error type:', typeof error);
      console.error('🗑️ Error details:', JSON.stringify(error, null, 2));
      message.error(`Failed to delete folder: ${error}`);
    }
  };

  const handleMoveDocument = async () => {
    if (!selectedDocForMove) return;
    
    try {
      await dispatch(moveDocument({ 
        documentId: selectedDocForMove.id, 
        folderId: targetFolderId 
      })).unwrap();
      message.success(`Moved "${selectedDocForMove.original_filename}" successfully`);
      setMoveModalVisible(false);
      setSelectedDocForMove(null);
      setTargetFolderId(null);
      await dispatch(fetchDocuments({}));
      await dispatch(fetchFolders());
    } catch (error) {
      message.error(`Failed to move document: ${error}`);
    }
  };

  const handleBatchMove = async () => {
    if (selectedRowKeys.length === 0) return;
    
    try {
      await Promise.all(
        selectedRowKeys.map(id => 
          dispatch(moveDocument({ documentId: id, folderId: targetFolderId })).unwrap()
        )
      );
      message.success(`Moved ${selectedRowKeys.length} document(s) successfully`);
      setMoveModalVisible(false);
      setSelectedRowKeys([]);
      setTargetFolderId(null);
      await dispatch(fetchDocuments({}));
      await dispatch(fetchFolders());
    } catch (error) {
      message.error(`Failed to move documents: ${error}`);
    }
  };

  const openMoveModal = (doc) => {
    setSelectedDocForMove(doc);
    setTargetFolderId(doc.folder_id);
    setMoveModalVisible(true);
  };

  const openVersionHistory = (doc) => {
    setSelectedDocForVersions(doc);
    setVersionDrawerVisible(true);
  };

  const handleVersionChange = () => {
    dispatch(fetchDocuments({}));
  };

  const handleCompare = (docId, versionId1, versionId2) => {
    setCompareVersions({ docId, v1: versionId1, v2: versionId2 });
    setCompareModalVisible(true);
  };

  const openPreviewModal = (doc) => {
    setSelectedDocForPreview(doc);
    setPreviewModalVisible(true);
  };

  const closePreviewModal = () => {
    setPreviewModalVisible(false);
    setSelectedDocForPreview(null);
  };

  const getFilteredDocuments = () => {
    if (viewMode === 'all') {
      return documents;
    } else if (viewMode === 'unfiled') {
      return documents.filter(d => !d.folder_id);
    } else if (viewMode === 'folder' && selectedFolderId) {
      return documents.filter(d => d.folder_id === selectedFolderId);
    }
    return documents;
  };

  const getFileIcon = (fileType) => {
    const iconStyle = { fontSize: '20px' };
    switch (fileType) {
      case 'pdf':
        return <FilePdfOutlined style={{ ...iconStyle, color: '#DC2626' }} />;
      case 'docx':
      case 'doc':
        return <FileWordOutlined style={{ ...iconStyle, color: '#0284C7' }} />;
      case 'xlsx':
      case 'xls':
        return <FileExcelOutlined style={{ ...iconStyle, color: '#16A34A' }} />;
      case 'pptx':
      case 'ppt':
        return <FilePptOutlined style={{ ...iconStyle, color: '#EA580C' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'tiff':
      case 'tif':
        return <FileImageOutlined style={{ ...iconStyle, color: '#0891B2' }} />;
      case 'md':
        return <FileMarkdownOutlined style={{ ...iconStyle, color: '#7C3AED' }} />;
      case 'csv':
        return <FileExcelOutlined style={{ ...iconStyle, color: '#16A34A' }} />;
      default:
        return <FileTextOutlined style={{ ...iconStyle, color: '#6B7280' }} />;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFolderName = (folderId) => {
    if (!folderId) return null;
    const folder = folders.find(f => f.id === folderId);
    return folder?.name;
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'original_filename',
      key: 'filename',
      sorter: (a, b) => a.original_filename.localeCompare(b.original_filename),
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            {getFileIcon(record.file_type)}
            <Text strong>{text}</Text>
          </Space>
          {record.folder_id && viewMode === 'all' && (
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '28px' }}>
              <FolderOutlined style={{ marginRight: '4px' }} />
              {getFolderName(record.folder_id)}
            </Text>
          )}
        </Space>
      ),
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'file_type',
      key: 'type',
      width: 100,
      filters: [
        { text: 'PDF', value: 'pdf' },
        { text: 'Word', value: 'docx' },
        { text: 'Word (Legacy)', value: 'doc' },
        { text: 'Excel', value: 'xlsx' },
        { text: 'PowerPoint', value: 'pptx' },
        { text: 'Text', value: 'txt' },
        { text: 'Markdown', value: 'md' },
        { text: 'CSV', value: 'csv' },
        { text: 'Image (JPG)', value: 'jpg' },
        { text: 'Image (JPEG)', value: 'jpeg' },
        { text: 'Image (PNG)', value: 'png' },
        { text: 'Image (TIFF)', value: 'tiff' },
      ],
      onFilter: (value, record) => record.file_type === value,
      render: (type) => (
        <Tag color={
          type === 'pdf' ? 'red' :
          type === 'docx' || type === 'doc' ? 'blue' :
          type === 'xlsx' ? 'green' :
          type === 'pptx' ? 'orange' :
          ['jpg', 'jpeg', 'png', 'tiff', 'tif'].includes(type) ? 'cyan' :
          type === 'md' ? 'purple' :
          type === 'csv' ? 'green' : 'default'
        }>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'file_size_bytes',
      key: 'size',
      width: 100,
      sorter: (a, b) => a.file_size_bytes - b.file_size_bytes,
      render: (bytes) => formatBytes(bytes),
    },
    {
      title: 'Chunks',
      dataIndex: 'chunk_count',
      key: 'chunks',
      width: 80,
      sorter: (a, b) => a.chunk_count - b.chunk_count,
      render: (count) => <Text type="secondary">{count}</Text>,
    },
    {
      title: 'Version',
      dataIndex: 'version_count',
      key: 'version',
      width: 90,
      render: (versionCount, record) => (
        <Tooltip title="View version history">
          <Button
            type="text"
            size="small"
            onClick={() => openVersionHistory(record)}
            style={{ padding: '0 8px' }}
          >
            <Badge 
              count={versionCount || 1} 
              style={{ 
                backgroundColor: versionCount > 1 ? '#1890ff' : '#d9d9d9',
                fontSize: '11px'
              }}
            />
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_processed',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Processed', value: true },
        { text: 'Processing', value: false },
      ],
      onFilter: (value, record) => record.is_processed === value,
      render: (isProcessed) => (
        isProcessed ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Processed
          </Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="processing">
            Processing
          </Tag>
        )
      ),
    },
    {
      title: 'Upload Date',
      dataIndex: 'upload_date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(a.upload_date) - new Date(b.upload_date),
      render: (date) => new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview document">
            <Button 
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openPreviewModal(record)}
            />
          </Tooltip>
          <Tooltip title="Version history">
            <Button 
              type="text"
              icon={<HistoryOutlined />}
              size="small"
              onClick={() => openVersionHistory(record)}
            />
          </Tooltip>
          <Tooltip title="Move to folder">
            <Button 
              type="text"
              icon={<SwapOutlined />}
              size="small"
              onClick={() => openMoveModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete document?"
            description="This will permanently delete the document and all its data."
            onConfirm={() => handleDelete(record.id, record.original_filename)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const uploadQueue = useSelector((state) => state.uploadQueue?.queue || []);
  const activeUploads = uploadQueue.filter(
    (item) => item.status === 'uploading' || item.status === 'processing' || item.status === 'queued'
  ).length;

  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: '.pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.pptx,.jpg,.jpeg,.png,.tiff,.tif',
    beforeUpload: handleBeforeUpload,
    showUploadList: false,
    disabled: false,
  };

  const filteredDocuments = getFilteredDocuments();
  const currentFolderName = viewMode === 'folder' && selectedFolderId 
    ? getFolderName(selectedFolderId) 
    : viewMode === 'unfiled' 
      ? 'Unfiled Documents' 
      : 'All Documents';

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          Document Library
        </Title>
        <Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: 0 }}>
          Organize and manage your technical documentation for AI-powered question answering
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={6}>
          <Card bordered={false} size="small">
            <FolderTree
              folders={folders}
              documents={documents}
              selectedFolderId={viewMode === 'folder' ? selectedFolderId : (viewMode === 'unfiled' ? 'unfiled' : null)}
              onSelectFolder={handleSelectFolder}
              onCreateFolder={handleCreateFolder}
              onDeleteFolder={handleDeleteFolder}
              loading={false}
            />
          </Card>
        </Col>

        <Col xs={24} md={18}>
          <Card bordered={false} style={{ marginBottom: '16px' }}>
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                {activeUploads > 0 ? (
                  <Badge count={activeUploads} offset={[10, 0]}>
                    <InboxOutlined style={{ color: '#0B3D91', fontSize: '36px' }} />
                  </Badge>
                ) : (
                  <UploadOutlined style={{ color: '#0B3D91', fontSize: '36px' }} />
                )}
              </p>
              <Title level={5} style={{ marginBottom: '4px' }}>
                {activeUploads > 0 
                  ? `${activeUploads} file(s) uploading...` 
                  : 'Click or drag files to upload'}
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: '13px' }}>
                <strong>Multiple files supported!</strong> PDF, Word, Excel, PowerPoint, Text, Markdown, CSV, Images (Max 100MB each)
                {viewMode === 'folder' && selectedFolderId && (
                  <span> - Will be added to <strong>{getFolderName(selectedFolderId)}</strong></span>
                )}
              </Paragraph>
            </Dragger>
          </Card>

          {selectedRowKeys.length > 0 && (
            <Alert
              message={`${selectedRowKeys.length} document(s) selected`}
              type="info"
              action={
                <Space>
                  <Button 
                    size="small" 
                    icon={<SwapOutlined />}
                    onClick={() => {
                      setSelectedDocForMove(null);
                      setTargetFolderId(null);
                      setMoveModalVisible(true);
                    }}
                  >
                    Move to Folder
                  </Button>
                  <Popconfirm
                    title={`Delete ${selectedRowKeys.length} document(s)?`}
                    description="This action cannot be undone."
                    onConfirm={handleBatchDelete}
                    okText="Delete All"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              }
              closable
              onClose={() => setSelectedRowKeys([])}
              style={{ marginBottom: '16px' }}
            />
          )}

          <Card 
            bordered={false}
            title={
              <Space>
                {viewMode === 'folder' ? <FolderOutlined /> : <FileTextOutlined />}
                <Text strong>{currentFolderName} ({filteredDocuments.length})</Text>
              </Space>
            }
          >
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredDocuments}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} documents`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={<FileTextOutlined style={{ fontSize: '64px', color: '#D1D5DB' }} />}
                    description={
                      <Space direction="vertical" size="small">
                        <Text type="secondary">
                          {viewMode === 'folder' 
                            ? `No documents in this folder`
                            : viewMode === 'unfiled'
                              ? 'No unfiled documents'
                              : 'No documents uploaded yet'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          Upload documents using the area above
                        </Text>
                      </Space>
                    }
                  />
                ),
              }}
            />
          </Card>

          {documents.length > 0 && (
            <Card 
              bordered={false} 
              style={{ marginTop: '16px' }}
              size="small"
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary" strong>Processing Information:</Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  <ExclamationCircleOutlined /> Documents are automatically processed for semantic search after upload
                </Text>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  <ExclamationCircleOutlined /> Processing typically takes 1-2 minutes depending on document size
                </Text>
              </Space>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title={selectedDocForMove ? `Move "${selectedDocForMove.original_filename}"` : `Move ${selectedRowKeys.length} document(s)`}
        open={moveModalVisible}
        onOk={selectedDocForMove ? handleMoveDocument : handleBatchMove}
        onCancel={() => {
          setMoveModalVisible(false);
          setSelectedDocForMove(null);
          setTargetFolderId(null);
        }}
        okText="Move"
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">Select destination folder:</Text>
        </div>
        <Select
          style={{ width: '100%' }}
          placeholder="Select a folder"
          value={targetFolderId}
          onChange={setTargetFolderId}
          allowClear
          options={[
            { 
              value: null, 
              label: (
                <Space>
                  <FileTextOutlined />
                  <span>Unfiled (No folder)</span>
                </Space>
              )
            },
            ...folders.map(folder => ({
              value: folder.id,
              label: (
                <Space>
                  <FolderOutlined style={{ color: '#0B3D91' }} />
                  <span>{folder.name}</span>
                </Space>
              )
            }))
          ]}
        />
      </Modal>

      <BulkUploadManager visible={true} />

      <VersionHistoryDrawer
        visible={versionDrawerVisible}
        onClose={() => {
          setVersionDrawerVisible(false);
          setSelectedDocForVersions(null);
        }}
        document={selectedDocForVersions}
        onVersionChange={handleVersionChange}
        onCompare={handleCompare}
      />

      <VersionCompareModal
        visible={compareModalVisible}
        onClose={() => {
          setCompareModalVisible(false);
          setCompareVersions({ docId: null, v1: null, v2: null });
        }}
        documentId={compareVersions.docId}
        versionId1={compareVersions.v1}
        versionId2={compareVersions.v2}
      />

      <DocumentPreviewModal
        visible={previewModalVisible}
        document={selectedDocForPreview}
        onClose={closePreviewModal}
      />
    </div>
  );
}

export default Documents;
